import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'motion/react';
import type { PointerEvent } from 'react';

import { coverGradient } from '../lib/cover';
import { cn } from '../lib/utils';

interface CoverCardProps {
  title: string;
  /** Issue number as printed; omitted for a series cover. */
  number?: string;
  subtitle?: string;
  imageUrl?: string;
  /** Owned issues are shown in colour, missing ones greyed out. */
  owned?: boolean;
  quantity?: number;
  onClick?: () => void;
}

/**
 * Cover tile of the collection grid. The pointer tilt and the sheen are the
 * one flourish this component allows itself, and both are skipped when the
 * user asked for reduced motion.
 */
export function CoverCard({
  title,
  number,
  subtitle,
  imageUrl,
  owned = true,
  quantity,
  onClick,
}: CoverCardProps) {
  const reduceMotion = useReducedMotion();

  const pointerX = useMotionValue(0.5);
  const pointerY = useMotionValue(0.5);
  const springX = useSpring(pointerX, { stiffness: 220, damping: 22 });
  const springY = useSpring(pointerY, { stiffness: 220, damping: 22 });

  const rotateY = useTransform(springX, [0, 1], [-8, 8]);
  const rotateX = useTransform(springY, [0, 1], [7, -7]);
  const sheenX = useTransform(springX, (value) => `${value * 100}%`);
  const sheenY = useTransform(springY, (value) => `${value * 100}%`);
  const sheen = useMotionTemplate`radial-gradient(circle at ${sheenX} ${sheenY}, rgba(255,255,255,0.18), transparent 55%)`;

  function handlePointerMove(event: PointerEvent<HTMLDivElement>) {
    if (reduceMotion) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    pointerX.set((event.clientX - bounds.left) / bounds.width);
    pointerY.set((event.clientY - bounds.top) / bounds.height);
  }

  function resetPointer() {
    pointerX.set(0.5);
    pointerY.set(0.5);
  }

  const Tag = onClick ? 'button' : 'div';

  return (
    <div
      className="[perspective:900px]"
      onPointerMove={handlePointerMove}
      onPointerLeave={resetPointer}
    >
      <motion.div
        style={reduceMotion ? undefined : { rotateX, rotateY, transformStyle: 'preserve-3d' }}
        whileHover={reduceMotion ? undefined : { scale: 1.03 }}
        transition={{ type: 'spring', stiffness: 260, damping: 24 }}
        className="group relative"
      >
        <Tag
          onClick={onClick}
          className={cn(
            'relative block w-full overflow-hidden rounded-panel border text-left',
            'aspect-[2/3] bg-surface-raised',
            owned ? 'border-border' : 'border-dashed opacity-55 grayscale',
            onClick && 'cursor-pointer',
          )}
          style={imageUrl ? undefined : { backgroundImage: coverGradient(title) }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={number ? `${title} ${number}` : title}
              loading="lazy"
              className="size-full object-cover"
            />
          ) : (
            <span className="absolute inset-0 halftone opacity-30" aria-hidden="true" />
          )}

          {number ? (
            <span className="absolute top-2 left-2 rounded-md bg-background/75 px-2 py-0.5 font-display text-sm text-foreground backdrop-blur-sm">
              {number}
            </span>
          ) : null}

          {/* Bottom right, so the top corners stay free for the number and for
              a selection control the list may draw on top. */}
          {quantity && quantity > 1 ? (
            <span className="absolute right-2 bottom-10 rounded-md bg-accent px-1.5 py-0.5 text-xs font-semibold text-accent-foreground">
              {quantity}×
            </span>
          ) : null}

          {/* Without artwork the title itself becomes the cover, so the caption
              below only repeats the metadata. */}
          {!imageUrl ? (
            <span className="absolute inset-x-3 top-1/2 -translate-y-1/2 text-center font-display text-xl leading-tight text-white/90 uppercase">
              {title}
            </span>
          ) : null}

          <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background/95 to-transparent px-2 pt-6 pb-2">
            {imageUrl ? (
              <span className="block truncate text-xs font-medium text-foreground">{title}</span>
            ) : null}
            {subtitle ? (
              <span className="block truncate text-[0.7rem] text-muted">{subtitle}</span>
            ) : null}
          </span>

          {!reduceMotion ? (
            <motion.span
              aria-hidden="true"
              style={{ backgroundImage: sheen }}
              className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            />
          ) : null}
        </Tag>
      </motion.div>
    </div>
  );
}
